import React, { useState, useEffect } from "react";
import { CoherenceDataGrid, CoherenceDataGridColumn, CoherenceLoading, CoherenceNotificationType, CoherencePanel, CoherencePanelSize } from "@coherence-design-system/controls";
import { DefaultButton, DetailsListLayoutMode, Dropdown, ICommandBarItemProps, IDropdownOption, Label, Link, Persona, PersonaSize, PrimaryButton, Stack, TooltipHost } from "@fluentui/react";

import { OPPApiHelper, isEmpty, onFormatDate, getEffectiveMonth, getFileVersion } from "../../utils/OPPApiHelper";
import { loadWorkbook, sheetToCsv, createCsvFile } from "../../shared/ExcelUtils/ExcelUtils";
import { GetUserRole } from "../../utils/GetUserRole";
import { useToast } from "../../hooks/useToast";
import { UnAuthorized } from "../../shared/UnAuthorized/UnAuthorized";
import ToastMessage from "../../shared/ToastMessage/ToastMessage";
import Accordion from "../../shared/Accordion/Accordion";
import CustomGridControls from "../../shared/CustomGridControls/CustomGridControls";
import CustomPagination from "../../shared/CutomPagination/CustomPagination";
import { MetadataManagementStyles } from "./MetadataManagement.styles";
import { getCurrentUserId } from "../../utils/CurrentUser";

interface FxRates {
  Id: number;
  FileName: string;
  FilePath: string;
  FullFilePath: string;
  FileType: string;
  CreatedBy: string;
  CreatedByUserName: string;
  CreatedDate: string;
  IsActive: boolean;
}

type UserPhotoMap = Record<string, string | null>;

export const getCreatedByDisplayName = (
  item: Pick<FxRates, "CreatedBy" | "CreatedByUserName">
): string => item.CreatedByUserName || item.CreatedBy || "-";

export const getMissingCreatedByObjectIds = (
  metadataRows: Array<Pick<FxRates, "CreatedBy">>,
  createdByPhotos: Record<string, string>
): string[] => [...new Set(metadataRows
  .map(row => row.CreatedBy)
  .filter(objectId => objectId && !(objectId in createdByPhotos)))];

export const MetadataManagement: React.FunctionComponent = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [LoadText, setLoadText] = useState<string>("");
  const [userRole, setUserRole] = useState(GetUserRole());
  const [showPanel, setShowPanel] = useState<boolean>(false);
  const [fileType, setFileType] = useState<string>('FX');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [csvListFile, setCsvListFile] = useState<File | null>(null);
  const [csvFxTypeFile, setCsvFxTypeFile] = useState<File | null>(null);
  const [csvERPFile, setCsvERPFile] = useState<File | null>(null);
  const [attachments, setAttachments] = useState<FxRates[]>([]);
  const [allAttachments, setAllAttachments] = useState<FxRates[]>([]);
  const [historyAttachments, setHistoryAttachments] = useState<FxRates[]>([]);
  const [createdByPhotos, setCreatedByPhotos] = useState<Record<string, string>>({});
  const [paginatedData, setPaginatedData] = useState<any>([]);
  const [paginatedHistoryData, setPaginatedHistoryData] = useState<any>([]);
  const [refreshPagination, setRefreshPagination] = useState<boolean>(false);
  const [refreshHistoryPagination, setRefreshHistoryPagination] = useState<boolean>(false);
  const [fileNameError, setFileNameError] = useState<string>("");
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [referenceDetailsAttributes, setReferenceDetailsAttributes] = useState<any>(null);
  const [referenceDetailsTitle, setReferenceDetailsTitle] = useState<string>("Reference Details");
  const { showToast, toastTitle, toastMessage, toastType, showToastMessage } = useToast();

  // Sort state
  const [sortColumnDetails, setSortColumnDetails] = useState<any>({ "columnName": "CreatedDate", "sortType": "desc" });

  const styles = MetadataManagementStyles;

  useEffect(() => {
    if (userRole.includes("OpsAdmin")) {
      setIsLoaded(true);
      loadAttachments("Loading Metadata Management...");
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const metadataRows = [...allAttachments, ...historyAttachments];
    const missingObjectIds = getMissingCreatedByObjectIds(metadataRows, createdByPhotos);
    if (missingObjectIds.length === 0) return;

    void (async () => {
      try {
        const photoMap = await OPPApiHelper("PORItems/GetUserPhotos", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(missingObjectIds)
        }) as UserPhotoMap;
        setCreatedByPhotos(current => ({
          ...current,
          ...Object.fromEntries(missingObjectIds.map(objectId => [objectId, photoMap?.[objectId] ?? ""]))
        }));
      } catch (error: unknown) {
        console.error("Failed to load Metadata Management user photos.", error);
        setCreatedByPhotos(current => ({
          ...current,
          ...Object.fromEntries(missingObjectIds.map(objectId => [objectId, current[objectId] ?? ""]))
        }));
      }
    })();
  }, [allAttachments, historyAttachments, createdByPhotos]);

  const loadAttachments = (text: string) => {
    setLoadText(text);
    setIsLoaded(true);
    setAttachments([]);
    setAllAttachments([]);
    const requestOptions = {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      mode: 'cors'
    };

    OPPApiHelper('MetadataManagement/GetMetadata', requestOptions)
      .then((res: FxRates[]) => {
        setIsLoaded(false);
        if (!isEmpty(res)) {
          const activeRecords = res.filter(r => r.IsActive);
          const inactiveRecords = res.filter(r => !r.IsActive);
          sortDefault(activeRecords);
          setAttachments(activeRecords);
          setAllAttachments(JSON.parse(JSON.stringify(activeRecords)));
          setHistoryAttachments(inactiveRecords);
          resetHistoryPagination();
        }
      })
      .catch((err) => {
        setIsLoaded(false);
        showToastMessage("Error", "Failed to load Metadata", CoherenceNotificationType.ERROR);
      });
  };

  // Sort helpers
  const sortDefault = (data: any) => {
    if (sortColumnDetails !== undefined && sortColumnDetails["columnName"] !== undefined) {
      onSorted(sortColumnDetails["columnName"], sortColumnDetails["sortType"], data);
    }
  };

  const appendSortIcon = (columnName: string): any => {
    if (sortColumnDetails !== undefined && sortColumnDetails["columnName"] === columnName) {
      return sortColumnDetails["sortType"] === "asc" ? "Ascending" : "Descending";
    }
  };

  const getMonthNumber = (fileName: string): number => {
    const match = fileName.match(/_\d{4}(\d{2})\d{2}\./);
    return match ? parseInt(match[1], 10) : 0;
  };

  const onSorted = (columnName: string, sortType: string, fxRatesData?: any): void => {
    setSortColumnDetails({ "columnName": columnName, "sortType": sortType });
    let sortedData = (fxRatesData !== undefined ? fxRatesData : attachments).sort((a: any, b: any) => {
      // Custom sort for EffectiveMonth — compare month numbers from filename
      if (columnName === 'EffectiveMonth') {
        const monthA = getMonthNumber(a.FileName);
        const monthB = getMonthNumber(b.FileName);
        if (monthA > monthB) return sortType === 'asc' ? 1 : -1;
        if (monthA < monthB) return sortType === 'asc' ? -1 : 1;
        return 0;
      }

      if (a[columnName] === null) return sortType === 'asc' ? 1 : -1;
      if (b[columnName] === null) return sortType === 'asc' ? -1 : 1;

      // Handle date values
      if (Date.parse(a[columnName]) && Date.parse(b[columnName])) {
        const dateA = new Date(a[columnName]);
        const dateB = new Date(b[columnName]);
        if (dateA > dateB) return sortType === 'asc' ? 1 : -1;
        if (dateA < dateB) return sortType === 'asc' ? -1 : 1;
      }
      if (a[columnName] > b[columnName]) return sortType === 'asc' ? 1 : -1;
      if (a[columnName] < b[columnName]) return sortType === 'asc' ? -1 : 1;
      return 0;
    });

    setAttachments(JSON.parse(JSON.stringify(sortedData)));
    resetPagination();
  };

  // Pagination helpers
  const resetPagination = () => {
    setRefreshPagination(refreshPagination ? false : true);
  };

  const updatePaginationData = (data: any) => {
    setPaginatedData(data);
  };

  const resetHistoryPagination = () => {
    setRefreshHistoryPagination(prev => !prev);
  };

  const updateHistoryPaginationData = (data: any) => {
    setPaginatedHistoryData(data);
  };

  const openReferenceDetailsModal = (item: FxRates) => {
    setReferenceDetailsAttributes(item);
    setReferenceDetailsTitle("Reference Details");
    setModalOpen(true);
  };

  const getReferenceDetailsAttributes = (): any => {
    if (!referenceDetailsAttributes) return null;
    const item = referenceDetailsAttributes as FxRates;
    const effectiveMonth = getEffectiveMonth(item.FileName);
    return (
      <div style={{ padding: '16px' }}>
        <div style={{ display: 'flex', marginBottom: '8px' }}>
          <div style={{ width: '40%', textAlign: 'left' }}><Label>Effective Month</Label></div>
          <div style={{ width: '60%', padding: '5px' }}> : {effectiveMonth}</div>
        </div>
      </div>
    );
  };

  const createSortColumns = () => {
    return [
      { "fieldName": "FileName", "label": "File Name" },
      { "fieldName": "FileType", "label": "File Type" },
      { "fieldName": "CreatedDate", "label": "Created Date" }
    ];
  };

  const createFilterColumns = () => {
    return [
      { "fieldName": "FileName", "label": "File Name", "type": "searchTextBox", "options": [], "additionalData": { "minChar": 2 } },
      { "fieldName": "FileType", "label": "File Type", "type": "searchDropdown", "options": [] },
      { "fieldName": "CreatedByUserName", "label": "Created By", "type": "searchDropdown", "options": [] },
      { "fieldName": "CreatedDate", "label": "Created Date", "type": "date", "fromCreatedDate": null, "toCreatedDate": null }
    ];
  };

  const onApplyFilter = (filteredRows: FxRates[]): void => {
    setAttachments(JSON.parse(JSON.stringify(filteredRows)));
    resetPagination();
  };

  const onClearFilter = (): void => {
    const restoredRows = JSON.parse(JSON.stringify(allAttachments));
    sortDefault(restoredRows);
  };

  // Command bar buttons
  const generateButtons = (): ICommandBarItemProps[] => {
    return [
      {
        key: 'Upload',
        text: 'Upload',
        ariaLabel: 'Upload',
        iconProps: { iconName: 'Upload' },
        onClick: () => setShowPanel(true)
      }
    ];
  };

  const onClickCommandBarsButtonsEvents = (source: any) => {
    if (source === 'Upload') {
      setShowPanel(true);
    }
  };

  // File handling
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFileNameError("");
    setCsvListFile(null);
    setCsvFxTypeFile(null);
    setCsvERPFile(null);
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];

      let dateStr = "";
      if (fileType === 'ERP') {
        // ERP: expected format is ERPGrid.xlsx
        if (!/^ERPGrid\.xlsx$/i.test(file.name)) {
          setFileNameError("File must be named ERPGrid.xlsx");
          setSelectedFile(null);
          return;
        }
      } else if (fileType === 'VBD') {
        // VBD: expected format is VBD_AreaPath_DeliveryDomain.xlsx
        if (!/^VBD_AreaPath_DeliveryDomain\.xlsx$/i.test(file.name)) {
          setFileNameError("File must be named VBD_AreaPath_DeliveryDomain.xlsx");
          setSelectedFile(null);
          return;
        }
      } else {
        // FX: Validate filename pattern: {name}_{YYYYMMDD}.xlsx
        const filenamePattern = /^.+_(\d{8})\.xlsx$/i;
        const match = file.name.match(filenamePattern);

        if (!match) {
          setFileNameError("File must be named in the format: {name}_{YYYYMMDD}.xlsx (e.g., Fxrule_20260301.xlsx)");
          setSelectedFile(null);
          return;
        }

        // Validate the date portion
        dateStr = match[1];
        const month = parseInt(dateStr.substring(4, 6), 10);
        const day = parseInt(dateStr.substring(6, 8), 10);

        if (month < 1 || month > 12) {
          setFileNameError("Invalid month in filename. Month must be between 01 and 12");
          setSelectedFile(null);
          return;
        }

        if (day < 1 || day > 31) {
          setFileNameError("Invalid day in filename. Day must be between 01 and 31");
          setSelectedFile(null);
          return;
        }
      }

      // Validate file name length
      if (file.name.length > 1000) {
        setFileNameError("File name must be less than 1000 characters");
        setSelectedFile(null);
        return;
      }

      // Validate file size (500 MB)
      if (file.size > 500 * 1024 * 1024) {
        setFileNameError("File size must be less than 500 MB");
        setSelectedFile(null);
        return;
      }

      // Read and validate xlsx
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const buffer = e.target?.result as ArrayBuffer;
          const workbook = await loadWorkbook(buffer);

          if (fileType === 'FX') {
            // FX: Validate sheet names
            const sheetNames = workbook.worksheets.map(ws => ws.name);
            const requiredSheets = ['Lists', 'OnlinePFAM-FX Type'];
            const missingSheets = requiredSheets.filter(s => !sheetNames.includes(s));

            if (missingSheets.length > 0) {
              setFileNameError(`The uploaded file is missing required sheet(s): ${missingSheets.join(', ')}. The file must contain both 'Lists' and 'OnlinePFAM-FX Type' sheets.`);
              setSelectedFile(null);
              return;
            }

            // Generate CSV files from sheets
            const listsSheet = workbook.getWorksheet('Lists')!;
            const fxTypeSheet = workbook.getWorksheet('OnlinePFAM-FX Type')!;

            const listsCsv = sheetToCsv(listsSheet);
            const fxTypeCsv = sheetToCsv(fxTypeSheet);

            const csvFxTypeGridFileName = `FXTypeGrid_${dateStr}.csv`;
            const csvFxRateGridFileName = `FXRateGrid_${dateStr}.csv`;

            setCsvListFile(createCsvFile(fxTypeCsv, csvFxTypeGridFileName));
            setCsvFxTypeFile(createCsvFile(listsCsv, csvFxRateGridFileName));
            setSelectedFile(file);
          } 
          else if (fileType === 'ERP') {
            // ERP: Skip sheet validation, convert first worksheet to CSV (all values as strings)
            const firstSheet = workbook.worksheets[0];
            const csvContent = sheetToCsv(firstSheet, true);
            const erpFileName = `ERPGrid.csv`;
            const erpCsvFile = createCsvFile(csvContent, erpFileName);
            setCsvERPFile(erpCsvFile);
            setSelectedFile(file);
          }
          else if (fileType === 'VBD') {
            // VBD: store the uploaded XLSX as-is (no CSV conversion)
            setSelectedFile(file);
          }
        } catch (error) {
          setFileNameError("Failed to read the Excel file. Please ensure it is a valid .xlsx file.");
          setSelectedFile(null);
        }
      };
      reader.onerror = () => {
        setFileNameError("Failed to read the file. Please try again.");
        setSelectedFile(null);
      };
      reader.readAsArrayBuffer(file);
    }
  };

  const handleSaveAttachment = async () => {
    if (!selectedFile) {
      setFileNameError("Please select a file");
      return;
    }

    if (fileType === 'FX') {
      if (!csvListFile || !csvFxTypeFile) {
        setFileNameError("CSV files could not be generated from the Excel sheets. Please re-select the file.");
        return;
      }
    } else if (fileType === 'ERP') {
      if (!csvERPFile) {
        setFileNameError("CSV file could not be generated from the Excel file. Please re-select the file.");
        return;
      }
    }

    const formData = new FormData();
    
    if (fileType === 'FX') {
      formData.append('files', selectedFile);
      formData.append('csvListFile', csvListFile!);
      formData.append('csvFxTypeFile', csvFxTypeFile!);
    } else if (fileType === 'ERP') {
      formData.append('files', selectedFile);
      formData.append('csvERPFile', csvERPFile!);
    } else if (fileType === 'VBD') {
      formData.append('files', selectedFile);
    }

    const fxRatesData = {
      FileName: selectedFile.name,
      FilePath: "",
      CreatedBy: getCurrentUserId() || "",
      FileType: fileType
    };

    formData.append('metadataJSON', JSON.stringify(fxRatesData));

    const requestOptions = {
      method: 'PUT',
      body: formData,
      mode: 'cors'
    };

    showToastMessage("Upload", `Uploading ${fileType} file...`, CoherenceNotificationType.PENDING);

    OPPApiHelper('MetadataManagement/SaveMetadata', requestOptions)
      .then(() => {
        showToastMessage("Success", `${fileType} file uploaded successfully`, CoherenceNotificationType.SUCCESS);
        setShowPanel(false);
        setSelectedFile(null);
        setCsvListFile(null);
        setCsvFxTypeFile(null);
        setCsvERPFile(null);
        loadAttachments("Refreshing data...");
      })
      .catch((err) => {
        showToastMessage("Error", `Failed to upload ${fileType} file`, CoherenceNotificationType.ERROR);
      });
  };

  const handlePanelClose = () => {
    setShowPanel(false);
    setSelectedFile(null);
    setCsvListFile(null);
    setCsvFxTypeFile(null);
    setCsvERPFile(null);
    setFileNameError("");
  };

  const handleFileTypeChange = (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
    if (option) {
      setFileType(option.key as string);
      setSelectedFile(null);
      setCsvListFile(null);
      setCsvFxTypeFile(null);
      setCsvERPFile(null);
      setFileNameError("");
    }
  };


  // Download attachment
  const viewAttachment = async (item: FxRates) => {
    if (item && item.FullFilePath && item.FileName) {
      try {
        showToastMessage("Download", `Downloading ${item.FileName}...`, CoherenceNotificationType.STANDARD);
        const response = await fetch(item.FullFilePath);
        if (!response.ok) {
          throw new Error(`Failed to fetch file: ${response.statusText} (Status Code: ${response.status})`);
        }
        const blob = await response.blob();
        const a = document.createElement('a');
        const objectURL = URL.createObjectURL(blob);
        a.href = objectURL;
        a.download = item.FileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(objectURL);
        showToastMessage("Download Complete", `${item.FileName} downloaded successfully!`, CoherenceNotificationType.SUCCESS);
      } catch (error) {
        showToastMessage("Download Failed", `Failed to download ${item.FileName}. Please try again.`, CoherenceNotificationType.ERROR);
      }
    }
  };

  // Display the original filename stored in the DB (no stripping needed for xlsx)
  const getDisplayFileName = (fileName: string): string => {
    return fileName;
  };

  // Grid columns
  const getColumns = (): CoherenceDataGridColumn<FxRates>[] => {
    return [
      {
        key: 'FileName',
        name: 'File Name',
        fieldName: 'FileName',
        type: 'link',
        url: 'FullFilePath',
        minColumnWidth: 300,
        maxWidth: 300,
        isResizable: true,
        ariaLabel: 'File Name',
        data: [],
        onRender: (item: FxRates) => {
          return <Link onClick={() => { viewAttachment(item); }}>
            {getDisplayFileName(item.FileName)}
          </Link>;
        },
        iconName: appendSortIcon('FileName'),
        iconClassName: "customSortIcon"
      },
      {
        key: 'FileType',
        name: 'File Type',
        fieldName: 'FileType',
        type: 'string',
        minColumnWidth: 80,
        maxWidth: 100,
        isResizable: true,
        ariaLabel: 'File Type',
        data: [],
        onRender: (item: FxRates) => {
          const fileType = item.FileType || 'FX';
          return <TooltipHost content={fileType}>
            <span style={{ fontSize: 14, color: 'inherit' }}>{fileType}</span>
          </TooltipHost>;
        },
        iconName: appendSortIcon('FileType'),
        iconClassName: "customSortIcon"
      },
      {
        key: 'ReferenceDetails',
        name: 'Reference Details',
        fieldName: 'FileName',
        type: 'link',
        url: 'FullFilePath',
        minColumnWidth: 120,
        maxWidth: 140,
        isResizable: true,
        ariaLabel: 'Reference Details',
        data: [],
        onRender: (item: FxRates) => {
          if ((item.FileType || 'FX') !== 'FX') return <span>-</span>;
          return <Link onClick={() => openReferenceDetailsModal(item)}>Reference Details</Link>;
        }
      },
      {
        key: 'Version',
        name: 'Version',
        fieldName: 'FilePath',
        type: 'string',
        minColumnWidth: 80,
        maxWidth: 100,
        isResizable: true,
        ariaLabel: 'Version',
        data: [],
        onRender: (item: FxRates) => {
          const version = getFileVersion(item.FilePath);
          return <TooltipHost content={`Version ${version}`}>
            <span style={{ fontSize: 14, color: 'inherit' }}>{version}</span>
          </TooltipHost>;
        }
      },
      {
        key: 'CreatedBy',
        name: 'Created By',
        fieldName: 'CreatedByUserName',
        type: 'string',
        minColumnWidth: 200,
        maxWidth: 250,
        isResizable: true,
        ariaLabel: 'Created By',
        data: [],
        onRender: (item: FxRates) => {
          const createdBy = getCreatedByDisplayName(item);
          if (!item.CreatedBy) {
            return <span>-</span>;
          }
          return <TooltipHost content={createdBy}>
            <Persona
              primaryText={createdBy}
              imageUrl={createdByPhotos[item.CreatedBy] || undefined}
              size={PersonaSize.size32}
            />
          </TooltipHost>;
        },
        iconName: appendSortIcon('CreatedByUserName'),
        iconClassName: "customSortIcon"
      },
      {
        key: 'CreatedDate',
        name: 'Created Date',
        fieldName: 'CreatedDate',
        type: 'string',
        minColumnWidth: 180,
        maxWidth: 220,
        isResizable: true,
        ariaLabel: 'Created Date',
        data: [],
        onRender: (item: FxRates) => {
          if (item.CreatedDate) {
            const formatted = onFormatDate(item.CreatedDate);
            return <TooltipHost content={formatted}>
              <span style={{ fontSize: 14, color: 'inherit' }}>{formatted}</span>
            </TooltipHost>;
          }
          return <span>-</span>;
        },
        iconName: appendSortIcon('CreatedDate'),
        iconClassName: "customSortIcon"
      }
    ];
  };

  if (!userRole.includes("OpsAdmin")) {
    return <UnAuthorized />;
  }

  return (
    <>
      <div style={{ minHeight: '100vh' }}>
        <h2>Metadata Management</h2>

        <Accordion title="Metadata" isExpandDefault={true}>
          <CustomGridControls
            defaultFilterSessionKey="MetadataManagementDefaultFilter"
            enableSavedFilters
            onClickAdditionalControls={onClickCommandBarsButtonsEvents}
            additionalControls={generateButtons()}
            filterColumns={createFilterColumns()}
            sortColumns={createSortColumns()}
            data={allAttachments}
            onApplyFilter={onApplyFilter}
            onSorted={onSorted}
            onClearFilter={onClearFilter}
          />

          {showToast && <ToastMessage showToast={showToast} type={toastType} message={toastMessage} title={toastTitle} />}

          <div style={{ minHeight: 425, height: 425, maxHeight: 425 }}>
            {!isLoaded ? (
              <CoherenceDataGrid
                listConfig={getColumns()}
                data={paginatedData}
                isScrollable={true}
                isSortable={false}
                sortByDefault={false}
                layoutMode={DetailsListLayoutMode.fixedColumns}
                ariaLabel={"Metadata List"}
              />
            ) : (
              <CoherenceLoading primaryText={LoadText} />
            )}
          </div>

          <CustomPagination
            refreshPagination={refreshPagination}
            updatePaginationData={updatePaginationData}
            data={attachments}
            preferenceKey="metadata-management-files"
          />
        </Accordion>

        <Accordion title="Metadata History" isExpandDefault={false}>
          <div style={{ minHeight: 425, height: 425, maxHeight: 425 }}>
            {!isLoaded ? (
              <CoherenceDataGrid
                listConfig={getColumns()}
                data={paginatedHistoryData}
                isScrollable={true}
                isSortable={false}
                sortByDefault={false}
                layoutMode={DetailsListLayoutMode.fixedColumns}
                ariaLabel={"Metadata History List"}
              />
            ) : (
              <CoherenceLoading primaryText={LoadText} />
            )}
          </div>

          <CustomPagination
            refreshPagination={refreshHistoryPagination}
            updatePaginationData={updateHistoryPaginationData}
            data={historyAttachments}
            preferenceKey="metadata-management-history"
          />
        </Accordion>

        <CoherencePanel
          titleText="Upload Metadata File"
          isOpen={showPanel}
          onDismiss={handlePanelClose}
          panelSize={CoherencePanelSize.medium}
          closeButtonAriaLabel="Close"
        >
          <div style={{ padding: '20px' }}>
            <div style={{ marginBottom: '20px' }}>
              <label htmlFor="fileTypeDropdown" style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                File Type
              </label>
              <Dropdown
                id="fileTypeDropdown"
                selectedKey={fileType}
                onChange={handleFileTypeChange}
                options={[
                  { key: 'FX', text: 'FX' },
                  { key: 'ERP', text: 'ERP' },
                  { key: 'VBD', text: 'VBD' }
                ]}
                styles={{ dropdown: { width: '100%' } }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label htmlFor="fileInput" style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                Select File
              </label>
              <input
                id="fileInput"
                type="file"
                accept=".xlsx"
                onChange={handleFileChange}
                style={{ display: 'block', width: '100%' }}
              />
              {selectedFile && (
                <div style={{ marginTop: '8px', fontSize: '12px', color: '#605E5C' }}>
                  <div>Selected: {selectedFile.name}</div>
                  {fileType === 'FX' && (
                    <div style={{ marginTop: '4px' }}>Effective Month: {getEffectiveMonth(selectedFile.name)}</div>
                  )}
                </div>
              )}
              {fileNameError && (
                <div className={styles.errorMessage}>
                  {fileNameError}
                </div>
              )}
            </div>

            <Stack horizontal tokens={{ childrenGap: 10 }}>
              <PrimaryButton
                text="Save"
                onClick={handleSaveAttachment}
                disabled={!selectedFile || fileNameError !== ""}
              />
              <DefaultButton text="Cancel" onClick={handlePanelClose} />
            </Stack>
          </div>
        </CoherencePanel>

        <CoherencePanel
          title={referenceDetailsTitle}
          titleText={referenceDetailsTitle}
          isOpen={modalOpen}
          onDismiss={() => setModalOpen(false)}
          panelSize={CoherencePanelSize.medium}
          closeButtonAriaLabel="Close"
        >
          {modalOpen && getReferenceDetailsAttributes()}
        </CoherencePanel>
      </div>
    </>
  );
};

export default MetadataManagement;
